import { useEffect, useState, useMemo } from 'react';
import { citiesFor } from '../cities';
import { t } from '../i18n';

const API = '/api/bloodbank';
function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}
const C = { bg: '#FCFCFA', surface: '#fff', border: '#E3E1D9', text: '#2C2C2A', muted: '#5F5E5A', primary: '#0F6E56', red: '#C0392B' };
const inp = { width: '100%', padding: '9px 11px', fontSize: 14, border: `0.5px solid ${C.border}`, borderRadius: 8, boxSizing: 'border-box', marginBottom: 8 };
const lbl = { fontSize: 12, color: C.muted, display: 'block', marginBottom: 3 };
const btn = { padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: C.red, color: '#fff', cursor: 'pointer' };
const btnGhost = { ...btn, background: '#fff', color: C.text, border: `0.5px solid ${C.border}` };
const tabBtn = (a) => ({ padding: '8px 12px', fontSize: 12.5, fontWeight: 500, border: 'none', borderBottom: a ? `2px solid ${C.red}` : '2px solid transparent', background: 'transparent', color: a ? C.red : C.muted, cursor: 'pointer' });
const GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

export default function BloodBank() {
  const [tab, setTab] = useState('find');
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: C.bg, minHeight: '100vh' }}>
      <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>🩸 {t('bb_title')}</h1>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 12px' }}>{t('bb_sub')}</p>
      <div style={{ display: 'flex', gap: 2, borderBottom: `0.5px solid ${C.border}`, marginBottom: 14, flexWrap: 'wrap' }}>
        <button style={tabBtn(tab === 'find')} onClick={() => setTab('find')}>{t('bb_find_donor')}</button>
        <button style={tabBtn(tab === 'directory')} onClick={() => setTab('directory')}>{t('bb_directory')}</button>
        <button style={tabBtn(tab === 'requests')} onClick={() => setTab('requests')}>{t('bb_requests')}</button>
        <button style={tabBtn(tab === 'donor')} onClick={() => setTab('donor')}>{t('bb_become_donor')}</button>
      </div>
      {tab === 'find' && <FindDonors />}
      {tab === 'directory' && <DonorDirectory />}
      {tab === 'requests' && <Requests />}
      {tab === 'donor' && (<><BecomeDonor /><DonorLocation /></>)}
      <p style={{ fontSize: 11, color: C.muted, marginTop: 20, fontStyle: 'italic', borderTop: `0.5px solid ${C.border}`, paddingTop: 10 }}>⚠️ {t('bb_disclaimer')}</p>
    </div>
  );
}

// Donor ne jo tareeqe allow kiye, sirf wohi buttons dikhein
// Profile se city / country / phone — dobara poochhne ki zaroorat nahi
function useProfileDefaults() {
  const [p, setP] = useState({ city: '', country: '', phone: '' });
  useEffect(() => {
    fetch('/api/master-profile/', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        const src = d.profile || d;
        setP({ city: src.city || '', country: src.country || '', phone: src.phone || '' });
      })
      .catch(() => {});
  }, []);
  return p;
}

// City ki dropdown. Country KHUD profile se leta hai — bahar se koi prop nahi,
// is liye har component me bila khatra chalta hai.
// Likha bhi ja sakta hai, lekin sab ek hijje likhein warna matching nahi hoti.
function CityInput({ value, onChange, style, placeholder }) {
  const p = useProfileDefaults();
  const list = citiesFor(p.country);
  return (
    <>
      <input
        style={style}
        value={value || ''}
        onChange={onChange}
        list="penflow-cities"
        placeholder={placeholder || t('bb_city_placeholder')}
        autoComplete="address-level2"
      />
      <datalist id="penflow-cities">
        {list.map((ct) => <option key={ct} value={ct} />)}
      </datalist>
    </>
  );
}

// Donor ne aakhri dafa kab diya, aur kya abhi de sakta hai.
// Aam usool: poora khoon dene ke baad ~3 mahine ka waqfa.
// Ye rehnumai hai, faisla nahi — asal faisla hospital karta hai.
function DonorStatus({ d }) {
  if (!d || d.can_donate === undefined) return null;

  const months = d.days_since != null ? Math.floor(d.days_since / 30) : null;
  const ago =
    d.days_since == null ? null
    : d.days_since < 30 ? `${d.days_since} ${t('bb_days_ago')}`
    : months < 12 ? `${months} ${t('bb_months_ago')}`
    : `${Math.floor(months / 12)}${t('bb_years_ago')}`;

  const wrap = { fontSize: 11, margin: '3px 0 0', display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' };

  if (d.donor_state === 'never_donated') {
    return (
      <p style={{ ...wrap, color: '#0F6E56' }}>
        <span>✅ {t('bb_can_donate')}</span>
        <span style={{ color: '#5F5E5A' }}>· {t('bb_no_record')}</span>
      </p>
    );
  }
  if (d.can_donate) {
    return (
      <p style={{ ...wrap, color: '#0F6E56' }}>
        <span>✅ {t('bb_can_donate')}</span>
        <span style={{ color: '#5F5E5A' }}>· {t('bb_last_gave')} {ago}</span>
      </p>
    );
  }
  return (
    <p style={{ ...wrap, color: '#9A6B00' }}>
      <span>⏳ {t('bb_too_soon')}</span>
      <span style={{ color: '#5F5E5A' }}>· {t('bb_from')} {d.eligible_on}</span>
    </p>
  );
}

function ContactButtons({ d }) {
  const wa = String(d.whatsapp_number || d.contact || '').replace(/[^0-9]/g, '');
  const base = { fontSize: 12, padding: '6px 12px', borderRadius: 8, textDecoration: 'none', display: 'inline-block' };
  const solid = { ...base, background: '#0F6E56', color: '#fff', border: 'none' };
  const ghost = { ...base, background: '#fff', color: '#2C2C2A', border: '0.5px solid #B4B2A9' };
  const none = d.allow_call === false && d.allow_sms === false && !d.allow_whatsapp;
  if (none) return <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>{t('bb_no_contact_pref')}</p>;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
      {d.allow_call !== false && <a href={`tel:${d.contact}`} style={solid}>📞 {t('bb_call')}</a>}
      {d.allow_sms !== false && <a href={`sms:${d.contact}`} style={ghost}>💬 {t('bb_sms')}</a>}
      {d.allow_whatsapp && wa && (
        <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" style={ghost}>🟢 WhatsApp</a>
      )}
      {d.username && (
        <a href={`/chat/${d.username}`} style={ghost}>💬 {t('bb_chat')}</a>
      )}
    </div>
  );
}

// Poori donor list — mulk -> sheher. Apna mulk sab se upar.
function DonorDirectory() {
  const [data, setData] = useState(null);
  const [bg, setBg] = useState('');
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');   // all | donors | needs
  const [open, setOpen] = useState({});

  useEffect(() => {
    const id = setTimeout(() => {
      const p = new URLSearchParams();
      if (bg) p.set('blood_group', bg);
      if (q) p.set('q', q);
      if (show !== 'all') p.set('show', show);
      getLoc((L) => { if (L) { p.set('lat', L.lat); p.set('lng', L.lng); } fetch(`${API}/donors/directory/?${p}`, { headers: authHeaders() })
        .then((r) => r.json()).then(setData)
        .catch(() => setData({ countries: [], total_donors: 0, total_needs: 0 })); });
    }, 350);
    return () => clearTimeout(id);
  }, [bg, q, show]);

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const chip = (g) => (
    <button key={g || 'all'} onClick={() => setBg(g)}
      style={{ fontSize: 12, padding: '5px 11px', borderRadius: 14, cursor: 'pointer',
        border: bg === g ? 'none' : '0.5px solid #B4B2A9',
        background: bg === g ? '#C0392B' : '#fff', color: bg === g ? '#fff' : '#2C2C2A',
        fontWeight: bg === g ? 600 : 400 }}>{g || t('bb_all')}</button>
  );

  const showBtn = (k, label) => (
    <button key={k} onClick={() => setShow(k)}
      style={{ fontSize: 12, padding: '6px 12px', border: 'none', cursor: 'pointer',
        borderBottom: show === k ? '2px solid #C0392B' : '2px solid transparent',
        background: 'none', color: show === k ? '#C0392B' : '#5F5E5A',
        fontWeight: show === k ? 600 : 400 }}>{label}</button>
  );

  return (
    <div>
      <input style={{ ...inp, marginBottom: 8 }} value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={t('bb_search_placeholder')} />

      <div style={{ display: 'flex', gap: 2, borderBottom: '0.5px solid #E3E1D9', marginBottom: 10 }}>
        {showBtn('all', t('bb_show_all'))}
        {showBtn('donors', `🩸 ${t('bb_show_donors')}${data ? ` (${data.total_donors ?? 0})` : ''}`)}
        {showBtn('needs', `🆘 ${t('bb_show_needs')}${data ? ` (${data.total_needs ?? 0})` : ''}`)}
      </div>

      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 }}>
        {chip('')}
        {['A+','A-','B+','B-','O+','O-','AB+','AB-'].map((g) => chip(g))}
      </div>

      {!data ? <p style={{ fontSize: 13, color: '#5F5E5A' }}>…</p>
        : !data.countries || data.countries.length === 0
          ? <p style={{ fontSize: 13, color: '#5F5E5A' }}>{t('bb_no_donors')}</p>
        : data.countries.map((co) => (
          <div key={co.country} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#F4F3EE', borderRadius: 8, padding: '7px 11px', marginBottom: 6 }}>
              <p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
                {co.country}
                {data.my_country && co.country.toLowerCase() === data.my_country.toLowerCase() && (
                  <span style={{ fontSize: 10, fontWeight: 400, color: '#0F6E56', marginLeft: 6 }}>· {t('bb_your_country')}</span>
                )}
              </p>
              <span style={{ fontSize: 11, color: '#5F5E5A' }}>
                {co.donor_count > 0 && `🩸 ${co.donor_count}`}
                {co.donor_count > 0 && co.need_count > 0 && ' · '}
                {co.need_count > 0 && `🆘 ${co.need_count}`}
              </span>
            </div>

            {co.cities.map((ct) => {
              const key = co.country + '|' + ct.city;
              const isOpen = open[key] !== false;
              return (
                <div key={key} style={{ marginLeft: 8, marginBottom: 8 }}>
                  <button onClick={() => toggle(key)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px 0',
                      fontSize: 13, fontWeight: 600, color: '#2C2C2A' }}>
                    {isOpen ? '▾' : '▸'} {ct.city}{' '}
                    <span style={{ fontWeight: 400, color: '#5F5E5A' }}>
                      ({ct.donor_count > 0 && `🩸${ct.donor_count}`}
                      {ct.donor_count > 0 && ct.need_count > 0 && ' '}
                      {ct.need_count > 0 && `🆘${ct.need_count}`})
                    </span>
                  </button>

                  {isOpen && (
                    <>
                      {/* jinhe khoon CHAHIYE — pehle, kyunke waqt ahem hai */}
                      {ct.needs.map((r) => (
                        <div key={'n' + r.id} style={{ background: '#FDF2F0',
                          border: `0.5px solid ${String(r.urgency).toLowerCase() === 'urgent' ? '#C0392B' : '#E8C9C3'}`,
                          borderRadius: 8, padding: '7px 10px', margin: '4px 0 4px 12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>
                              {String(r.urgency).toLowerCase() === 'urgent' ? '🚨 ' : '🆘 '}{r.name}
                            </p>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#C0392B' }}>{r.blood_group}</span>
                          </div>
                          <p style={{ fontSize: 11, color: '#5F5E5A', margin: '1px 0 0' }}>
                            {t('bb_needs')} {r.units_needed} {t('bb_units')}
                            {r.hospital ? ` · ${r.hospital}` : ''} · {r.created_at}
                          </p>
                          {r.note && <p style={{ fontSize: 11, color: '#5F5E5A', margin: '2px 0 0' }}>{r.note}</p>}
                          <ContactButtons d={r} />
                        </div>
                      ))}

                      {/* jo khoon DE sakte hain */}
                      {ct.donors.map((d) => (
                        <div key={'d' + d.id} style={{ background: '#fff', border: '0.5px solid #E3E1D9',
                          borderRadius: 8, padding: '7px 10px', margin: '4px 0 4px 12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>🩸 {d.name}</p>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#C0392B' }}>{d.blood_group}</span>{d.distance_km != null && <span style={{ fontSize: 11, color: '#0891b2', marginLeft: 6 }}>📍 ~{d.distance_km} km</span>}
                          </div>
                          <p style={{ fontSize: 11, color: '#5F5E5A', margin: '1px 0 0' }}>{d.contact}</p>
                          <DonorStatus d={d} />
                          <ContactButtons d={d} />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
    </div>
  );
}


function FindDonors() {
  const prof = useProfileDefaults();
  const [donors, setDonors] = useState(null);
  const [bg, setBg] = useState('');
  const [city, setCity] = useState('');
  useEffect(() => { if (prof.city && !city) setCity(prof.city); }, [prof.city]);
  const load = () => getLoc((L) => fetch(`${API}/donors/?blood_group=${encodeURIComponent(bg)}&city=${encodeURIComponent(city)}${L ? `&lat=${L.lat}&lng=${L.lng}` : ''}`).then((r) => r.json()).then((d) => { const ds = d.donors || []; ds.sort((x, y) => (x.distance_km ?? 9e9) - (y.distance_km ?? 9e9)); setDonors(ds); }));
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select style={{ ...inp, marginBottom: 0, flex: 1 }} value={bg} onChange={(e) => setBg(e.target.value)}>
          <option value="">{t('bb_blood_group')}</option>
          {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <input style={{ ...inp, marginBottom: 0, flex: 1 }} placeholder={t('bb_city')} value={city} onChange={(e) => setCity(e.target.value)} />
        <button style={btnGhost} onClick={load}>{t('bb_search')}</button>
      </div>
      {donors === null && <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>}
      {donors && donors.length === 0 && <p style={{ fontSize: 13, color: C.muted }}>{t('bb_no_donors')}</p>}
      {donors && donors.map((d, i) => (
        <div key={i} style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '0.85rem 1rem', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 2px' }}>{d.name} <span style={{ color: C.red, fontWeight: 700 }}>{d.blood_group}</span>{d.distance_km != null && <span style={{ fontSize: 11, color: '#0891b2', marginLeft: 6, fontWeight: 400 }}>📍 ~{d.distance_km} km</span>}</p>
            <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>📍 {d.city}</p>
          </div>
          <ContactButtons d={d} />
        </div>
      ))}
    </div>
  );
}

function Requests() {
  const prof = useProfileDefaults();
  const [reqs, setReqs] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ patient_name: '', blood_group: 'O+', city: '', hospital: '', units_needed: 1, urgency: 'normal', contact: '', note: '' });
  useEffect(() => {
    // f null ho sakta hai jab tak donor ka record load na ho — pehle wo dekho
    setForm((f) => {
      if (!f) return f;
      return { ...f, city: f.city || prof.city, contact: f.contact || prof.phone };
    });
  }, [prof.city, prof.phone]);

  // Request lagane se PEHLE dekho — usi group ka donor pehle se maujood hai?
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    if (!form.blood_group) { setMatches(null); return; }
    const id = setTimeout(() => {
      fetch(`${API}/donors/match/?blood_group=${encodeURIComponent(form.blood_group)}&city=${encodeURIComponent(form.city || '')}`)
        .then((r) => r.json())
        .then((d) => setMatches(d))
        .catch(() => setMatches(null));
    }, 400);
    return () => clearTimeout(id);
  }, [form.blood_group, form.city]);

  const [msg, setMsg] = useState('');
  const load = () => fetch(`${API}/requests/`).then((r) => r.json()).then((d) => setReqs(d.requests || []));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const submit = async () => {
    if (!form.city || !form.contact) { setMsg(t('bb_city_contact_required')); return; }
    const res = await fetch(`${API}/requests/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
    const d = await res.json();
    if (d.success) { setAdding(false); setForm({ patient_name: '', blood_group: 'O+', city: '', hospital: '', units_needed: 1, urgency: 'normal', contact: '', note: '' }); load(); }
    else setMsg(d.error || 'Failed');
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div>
      {!adding && <button style={{ ...btn, marginBottom: 14 }} onClick={() => setAdding(true)}>➕ {t('bb_post_request')}</button>}
      {adding && (
        <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1rem', marginBottom: 14 }}>
          <label style={lbl}>{t('bb_patient_name')}</label><input style={inp} value={form.patient_name} onChange={set('patient_name')} />
          <label style={lbl}>{t('bb_blood_group')}</label>
          <select style={inp} value={form.blood_group} onChange={set('blood_group')}>{GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}</select>
          <label style={lbl}>{t('bb_city')}</label><CityInput style={inp} value={form.city} onChange={set('city')} />
          <label style={lbl}>{t('bb_hospital')}</label><input style={inp} value={form.hospital} onChange={set('hospital')} />
          <label style={lbl}>{t('bb_urgency')}</label>
          <select style={inp} value={form.urgency} onChange={set('urgency')}><option value="normal">{t('bb_normal')}</option><option value="urgent">{t('bb_urgent')}</option></select>
          <label style={lbl}>{t('bb_contact')}</label><input style={inp} value={form.contact} onChange={set('contact')} />
          {matches && matches.donors && matches.donors.length > 0 && (
            <div style={{ background: '#EAF6F1', border: '0.5px solid #0F6E56', borderRadius: 10, padding: '10px 12px', margin: '10px 0' }}>
              <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 2px', color: '#0F6E56' }}>
                ✅ {matches.donors.length} {matches.blood_group} {t('bb_donors_already')}
              </p>
              <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 8px' }}>
                {matches.same_city > 0
                  ? `${matches.same_city} ${t('bb_in_your_city')}`
                  : t('bb_other_cities_only')}
                {' — '}{t('bb_try_them_first')}
              </p>
              {matches.donors.slice(0, 5).map((d) => (
                <div key={d.id} style={{ background: '#fff', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>
                    {d.name} <span style={{ color: '#C0392B', fontWeight: 700 }}>{d.blood_group}</span>
                    <span style={{ fontSize: 11, color: '#5F5E5A', fontWeight: 400 }}> · {d.city}</span>
                  </p>
                  <DonorStatus d={d} />
                  <ContactButtons d={d} />
                </div>
              ))}
              <p style={{ fontSize: 11, color: '#5F5E5A', margin: '6px 0 0' }}>{t('bb_or_post_below')}</p>
            </div>
          )}

          <label style={lbl}>{t('bb_note')}</label><input style={inp} value={form.note} onChange={set('note')} />
          {msg && <p style={{ fontSize: 12, color: C.red }}>{msg}</p>}
          <button style={btn} onClick={submit}>{t('bb_submit')}</button>{' '}
          <button style={btnGhost} onClick={() => setAdding(false)}>✕</button>
        </div>
      )}
      {reqs === null && <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>}
      {reqs && reqs.length === 0 && <p style={{ fontSize: 13, color: C.muted }}>{t('bb_no_requests')}</p>}
      {reqs && reqs.map((r) => (
        <div key={r.id} style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderLeft: `4px solid ${r.urgency === 'urgent' ? C.red : C.border}`, borderRadius: 10, padding: '0.85rem 1rem', marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 2px' }}><span style={{ color: C.red, fontWeight: 700 }}>{r.blood_group}</span> · {r.city}{r.urgency === 'urgent' && <span style={{ fontSize: 11, color: C.red, marginLeft: 6 }}>🚨 {t('bb_urgent')}</span>}</p>
              <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{r.hospital && `🏥 ${r.hospital} · `}{r.units_needed} units{r.note ? ` · ${r.note}` : ''}</p>
            </div>
            <a href={`tel:${r.contact}`} style={{ ...btn, textDecoration: 'none' }}>📞 {t('bb_call')}</a>
          </div>
        </div>
      ))}
    </div>
  );
}

// "Maine aaj diya" — donor apni tareekh khud theek karta hai.
// Bina is ke last_donation purani reh jati hai aur zaroorat-mand
// aise donor ko call karte hain jo abhi de hi nahi sakta.
function IDonated({ last, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showDate, setShowDate] = useState(false);
  const [when, setWhen] = useState('');

  const send = async (dateStr) => {
    setBusy(true); setMsg('');
    try {
      const r = await fetch(`${API}/donors/donated/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify(dateStr ? { date: dateStr } : {}),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'Failed');
      setMsg('✅ ' + t('bb_thank_you_donor'));
      setShowDate(false);
      if (onDone) onDone(d);
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };

  return (
    <div style={{ background: '#FDF2F0', border: '0.5px solid #E8C9C3', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
      <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 3px', color: '#C0392B' }}>🩸 {t('bb_did_you_donate')}</p>
      <p style={{ fontSize: 11.5, color: '#5F5E5A', margin: '0 0 10px', lineHeight: 1.5 }}>
        {last ? `${t('bb_we_have')} ${last}. ${t('bb_keep_it_right')}` : t('bb_no_record_yet')}
      </p>

      {!showDate ? (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <button onClick={() => send(null)} disabled={busy}
            style={{ fontSize: 12.5, padding: '7px 14px', border: 'none', borderRadius: 8, background: '#C0392B', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {busy ? '…' : t('bb_donated_today')}
          </button>
          <button onClick={() => setShowDate(true)}
            style={{ fontSize: 12.5, padding: '7px 12px', border: '0.5px solid #B4B2A9', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
            {t('bb_donated_other_day')}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="date" value={when} onChange={(e) => setWhen(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            style={{ padding: '6px 9px', border: '0.5px solid #B4B2A9', borderRadius: 8, fontSize: 13 }} />
          <button onClick={() => when && send(when)} disabled={busy || !when}
            style={{ fontSize: 12.5, padding: '7px 14px', border: 'none', borderRadius: 8, background: '#C0392B', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {t('bb_save')}
          </button>
          <button onClick={() => setShowDate(false)}
            style={{ fontSize: 12.5, padding: '7px 12px', border: 'none', background: 'none', cursor: 'pointer', color: '#5F5E5A' }}>
            {t('bb_cancel')}
          </button>
        </div>
      )}

      {msg && <p style={{ fontSize: 12, margin: '8px 0 0', color: msg.startsWith('✅') ? '#0F6E56' : '#C0392B' }}>{msg}</p>}
    </div>
  );
}

function BecomeDonor() {
  const prof = useProfileDefaults();
  const [form, setForm] = useState(null);
  useEffect(() => {
    // f null ho sakta hai jab tak donor ka record load na ho — pehle wo dekho
    setForm((f) => {
      if (!f) return f;
      return { ...f, city: f.city || prof.city, contact: f.contact || prof.phone };
    });
  }, [prof.city, prof.phone]);

  const [msg, setMsg] = useState('');
  useEffect(() => {
    fetch(`${API}/donors/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => {
      setForm(d.exists ? d : { name: '', blood_group: 'O+', city: '', contact: '', last_donation: '', is_available: true });
    });
  }, []);
  if (!localStorage.getItem('token')) return <p style={{ fontSize: 13 }}>{t('cv_please_login')}</p>;
  if (!form) return <p style={{ fontSize: 13, color: C.muted }}>{t('loading')}</p>;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.city || !form.contact) { setMsg('City aur contact zaroori'); return; }
    const res = await fetch(`${API}/donors/mine/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
    const d = await res.json();
    setMsg(d.success ? '✅' : (d.error || 'Failed'));
  };
  return (
    <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '1.2rem', maxWidth: 460 }}>
      {form.exists && (
        <IDonated last={form.last_donation} onDone={(d) => setForm((f) => ({ ...f, last_donation: d.last_donation }))} />
      )}
      <label style={lbl}>Name</label><input style={inp} value={form.name} onChange={set('name')} />
      <label style={lbl}>{t('bb_blood_group')}</label>
      <select style={inp} value={form.blood_group} onChange={set('blood_group')}>{GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}</select>
      <label style={lbl}>{t('bb_city')}</label><CityInput style={inp} value={form.city} onChange={set('city')} />
      <label style={lbl}>{t('bb_contact')}</label><input style={inp} value={form.contact} onChange={set('contact')} />
      <label style={lbl}>{t('bb_last_donation')}</label><input type="date" style={inp} value={form.last_donation || ''} onChange={set('last_donation')} />
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <input type="checkbox" checked={form.is_available} onChange={(e) => setForm({ ...form, is_available: e.target.checked })} /> {t('bb_available')}
      </label>
      {msg && <span style={{ fontSize: 13, color: C.primary }}>{msg}</span>}
      <div><button style={btn} onClick={save}>{t('bb_save')}</button></div>
    </div>
  );
}


// ===== Donor distance (privacy-first) =====
let __bbLoc; let __bbLocDone = false;
function getLoc(cb) {
  if (__bbLocDone) { cb(__bbLoc); return; }
  if (!navigator.geolocation) { __bbLocDone = true; __bbLoc = null; cb(null); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => { __bbLoc = { lat: Math.round(pos.coords.latitude * 100) / 100, lng: Math.round(pos.coords.longitude * 100) / 100 }; __bbLocDone = true; cb(__bbLoc); },
    () => { __bbLoc = null; __bbLocDone = true; cb(null); },
    { timeout: 6000, maximumAge: 600000 }
  );
}

function DonorLocation() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => fetch(`${API}/donors/location/`, { headers: authHeaders() }).then((r) => r.json()).then(setSt).catch(() => setSt({ has_donor: false }));
  useEffect(() => { load(); }, []);
  if (!st || !st.has_donor) return null;
  const toggle = () => {
    setBusy(true);
    const enabling = !st.share_location;
    const post = (lat, lng) => fetch(`${API}/donors/location/`, {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ share_location: enabling, lat, lng }),
    }).then(() => { setBusy(false); load(); });
    if (enabling) {
      getLoc((L) => {
        if (!L) alert(t('bb_gps_fail'));
        post(L ? L.lat : null, L ? L.lng : null);
      });
    } else { post(null, null); }
  };
  return (
    <div style={{ background: '#fff', border: '0.5px solid #E2E8F0', borderRadius: 12, padding: '0.9rem', marginTop: 10 }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13.5, fontWeight: 600 }}>
        <input type="checkbox" checked={!!st.share_location} disabled={busy} onChange={toggle} style={{ marginTop: 2 }} />
        <span>{t('bb_share_loc')}</span>
      </label>
      <p style={{ fontSize: 11.5, color: '#64748B', margin: '6px 0 0 24px', lineHeight: 1.5 }}>{t('bb_share_help')}</p>
      {st.share_location && <p style={{ fontSize: 11.5, color: '#059669', margin: '4px 0 0 24px' }}>✅ {t('bb_loc_saved')}{st.has_coords ? '' : ' · ' + t('bb_gps_fail')}</p>}
    </div>
  );
}
